import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import { formatPhoneWithCountryCode } from "./phoneFormatter";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

/**
 * Fetch company logo as a Data URL
 * @param {string} company_code - Company code
 * @returns {Promise<string|null>} - Data URL or null if not found
 */
export const fetchCompanyLogo = async (company_code) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/admin/get-company-logo?company_code=${company_code}`
    );

    if (!response.ok) {
      console.warn(`Logo API fetch failed with status ${response.status}`);
      return null;
    }

    const data = await response.json();

    if (!data.success || !data.logoUrl) {
      return null;
    }

    const logoUrl = data.logoUrl;
    const fullLogoUrl = logoUrl.startsWith("http")
      ? logoUrl
      : `${BASE_URL}${logoUrl}`;

    const imageResponse = await fetch(fullLogoUrl);

    if (!imageResponse.ok) {
      return null;
    }

    const blob = await imageResponse.blob();

    if (blob.size === 0) {
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        resolve(dataUrl);
      };
      reader.onerror = () => {
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

/**
 * Generate 4x4 inch Packing Label PDF (compact, aligned, no overlap)
 * @param {Object} order - Order data including shipping and company info
 * @param {Array} items - Order items
 * @param {string} [logoDataUrl]
 */
export const generatePackingLabelPDF = (order, items = [], logoDataUrl) => {
  // 1 inch = 72 pt → 4 inch = 288 pt
  const pageSize = 4 * 72;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: [pageSize, pageSize], // 4x4 inch PDF
  });

  // Margins and scaling
  const margin = 10;
  const contentWidth = pageSize - margin * 2;
  let currentY = margin;

  // Helper for wrapped text
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 9) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line, i) => doc.text(line, x, y + i * lineHeight));
    return y + lines.length * lineHeight;
  };

  // Light gray header background helper
  const drawHeader = (label, x, y, width, height) => {
    doc.setFillColor(245, 245, 245);
    doc.rect(x, y, width, height, "F");
    doc.setFontSize(8);
    doc.setFont(undefined, "bold");
    doc.text(label, x + 4, y + height - 4);
  };

  // Split the 4x4 into balanced vertical layout (increased FROM/TO section)
  const sectionH = (contentWidth - 4 * margin) / 2.5;  // Increased from /3 to /2.5
  const colWidth = (contentWidth - margin) / 2;

  // ------------------- FROM / TO -------------------
  const fromX = margin;
  const fromY = currentY;
  const fromW = colWidth - 4;
  const toX = fromX + colWidth + 4;

  // Headers
  drawHeader("FROM:", fromX, fromY, fromW, 14);
  drawHeader("TO:", toX, fromY, fromW, 14);

  doc.setFontSize(7);
  doc.setFont(undefined, "normal");

  // FROM block
  let y1 = fromY + 25;
  y1 = addWrappedText(
    order.company_name || "Company",
    fromX + 5,
    y1,
    fromW - 10
  );
  y1 = addWrappedText(
    order.company_address || "Address",
    fromX + 5,
    y1 + 3,
    fromW - 10
  );
  if (order.company_phone) {
    const phone = formatPhoneWithCountryCode(
      order.company_country_code,
      order.company_phone
    );
    doc.text(phone, fromX + 5, y1 + 3);
  }

  // TO block
  let y2 = fromY + 25;
  const toFullName =
    `${order.shipping_first_name || ""} ${
      order.shipping_last_name || ""
    }`.trim() || "Name";
  y2 = addWrappedText(toFullName, toX + 5, y2, fromW - 10);
  
  // Display address line 1
  if (order.address_line_1) {
    y2 = addWrappedText(order.address_line_1, toX + 5, y2 + 3, fromW - 10);
  }
  
  // Display address line 2
  if (order.address_line_2) {
    y2 = addWrappedText(order.address_line_2, toX + 5, y2 + 3, fromW - 10);
  }
  
  // Display city and state
  y2 = addWrappedText(
    `${order.city || ""}, ${order.state || ""}`,
    toX + 5,
    y2 + 3,
    fromW - 10
  );
  
  // Display phone number
  if (order.phone) {
    const phone = formatPhoneWithCountryCode(
      order.country_code,
      order.phone
    );
    y2 = addWrappedText(phone, toX + 5, y2 + 3, fromW - 10);
  }

  // Divider line
  currentY += sectionH - 5;
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, currentY, pageSize - margin, currentY);

  // ------------------- ORDER DETAILS -------------------
  const orderY = currentY + 8;
  drawHeader("ORDER DETAILS:", margin, orderY, contentWidth / 2 - 3, 14);
  drawHeader(
    "PACKAGE INFO:",
    margin + contentWidth / 2 + 3,
    orderY,
    contentWidth / 2 - 3,
    14
  );

  doc.setFontSize(7);
  doc.setFont(undefined, "normal");

  // ORDER DETAILS
  let oy = orderY + 24;
  doc.text(`Order #: ${order.order_number || "N/A"}`, margin + 5, oy);
  oy += 11;
  doc.text(
    `Date: ${
      order.created_at ? new Date(order.created_at).toLocaleDateString() : "N/A"
    }`,
    margin + 5,
    oy
  );
  oy += 11;
  doc.text(`Items: ${items.length}`, margin + 5, oy);
  oy += 11;
  const totalQty = items.reduce(
    (sum, it) => sum + (parseInt(it.quantity) || 0),
    0
  );
  doc.text(`Qty: ${totalQty}`, margin + 5, oy);

  // PACKAGE INFO
  let py = orderY + 24;
  const px = margin + contentWidth / 2 + 8;

  doc.text("Weight: __________ kg", px, py);
  py += 16;

  doc.text("Dimensions: ________ cm", px, py);
  py += 16;

  doc.text("Packed By: __________________", px, py);
  py += 16;

  doc.text("Date: _______________________", px, py);

  // Divider
  currentY = orderY + sectionH;
  doc.line(margin, currentY, pageSize - margin, currentY);

  // ------------------- FRAGILE -------------------
  const fragileY = currentY + 10;
  drawHeader("FRAGILE / HANDLING NOTES:", margin, fragileY, contentWidth, 14);
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");

  const cb = 8;
  const fx = margin + 6;
  let fy = fragileY + 24;

  doc.rect(fx, fy - 6, cb, cb);
  doc.text("Fragile", fx + cb + 4, fy);

  doc.rect(fx + 60, fy - 6, cb, cb);
  doc.text("Handle with care", fx + 60 + cb + 4, fy);

  doc.rect(fx + 150, fy - 6, cb, cb);
  doc.text("Standard", fx + 150 + cb + 4, fy);

  // Border outline
  // Border outline
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);
  doc.rect(margin / 2, margin / 2, pageSize - margin, pageSize - margin);

  // ------------------- BARCODE AREA -------------------
  const barcodeY = pageSize - 55; // position near bottom
  const centerX = pageSize / 2;
  const orderText = String(order.order_number || "000000");

  // Create a hidden canvas for the barcode
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, orderText, {
    format: "CODE128",
    lineColor: "#000",
    width: 1.2,
    height: 30,
    displayValue: false,
    margin: 0,
  });

  // Embed barcode image
  const barcodeDataUrl = canvas.toDataURL("image/png");
  const barcodeWidth = 120;
  const barcodeHeight = 30;
  doc.addImage(
    barcodeDataUrl,
    "PNG",
    centerX - barcodeWidth / 2,
    barcodeY,
    barcodeWidth,
    barcodeHeight
  );

  // Add "Order #:" label below barcode
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  const label = `${orderText}`;
  const labelWidth = doc.getTextWidth(label);
  doc.text(label, centerX - labelWidth / 2, barcodeY + barcodeHeight + 10);

  // Save PDF
  doc.save(`PackingLabel-${orderText || "4x4"}.pdf`);
};
